import podLogo from '@/shared/publish/ui/assets/images/ttukttak/common/icon/logo_w.svg';
import downloadFileIcon from '@/shared/publish/ui/assets/images/ttukttak/common/icon/download.svg';
import hwpFileIcon from '@/shared/publish/ui/assets/images/ttukttak/common/icon/hwp.svg';
import pdfFileIcon from '@/shared/publish/ui/assets/images/ttukttak/common/icon/pdf.svg';
import wordFileIcon from '@/shared/publish/ui/assets/images/ttukttak/common/icon/word.svg';
import { Dropdown } from '@/shared/ui';
import { ContactPodButton } from '@/features/contact';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PodV2ArticleOption, PodV2DocumentType, PodV2SheetType } from '../model/pod-v2.types';
import { PodV2ContactPopup } from './pod-v2-contact-popup';
import './pod-v2-command-bar.scss';

type PodV2CommandBarProps = {
    articleOptions: PodV2ArticleOption[];
    dan: 1 | 2;
    disabled: boolean;
    exitUrl: string;
    includeAnswer: boolean;
    includeExplanation: boolean;
    includeTranslation: boolean;
    isEnglishSubject: boolean;
    publishData?: API.IntegPubDetail.Res;
    onAnswerChange: (include: boolean) => void;
    onDanChange: (dan: 1 | 2) => void;
    onDownload: (documentType: PodV2DocumentType) => void;
    onExplanationChange: (include: boolean) => void;
    onTranslationChange: (include: boolean) => void;
    onSelectSet: (setId: string) => void;
    onSheetChange: (sheet: PodV2SheetType) => void;
    selectedSetId: string;
    setIds: string[];
    setTitles: string[];
    sheet: PodV2SheetType;
    showDanControls: boolean;
    status: string;
    title: string;
};

const ChevronIcon = () => (
    <svg className="pod-v2-command-bar__chevron" viewBox="0 0 24 24" aria-hidden="true">
        <path d="m7 10 5 5 5-5" />
    </svg>
);

const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 18v2h14v-2" />
    </svg>
);

const ExitIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M10 5H6.5v13H10" />
        <path d="M13 8.5 17 12l-4 3.5" />
        <path d="M9 12h8" />
    </svg>
);

export const PodV2CommandBar = ({
    articleOptions,
    dan,
    disabled,
    exitUrl,
    includeAnswer,
    includeExplanation,
    includeTranslation,
    isEnglishSubject,
    publishData,
    onAnswerChange,
    onDanChange,
    onDownload,
    onExplanationChange,
    onTranslationChange,
    onSelectSet,
    onSheetChange,
    selectedSetId,
    setIds,
    setTitles,
    sheet,
    showDanControls,
    status,
    title,
}: PodV2CommandBarProps) => {
    const [isSolutionOpen, setIsSolutionOpen] = useState(false);
    const [isDownloadOpen, setIsDownloadOpen] = useState(false);
    const commandBarRef = useRef<HTMLElement>(null);

    const paperOptions = useMemo(
        () =>
            setIds.length > 0
                ? setIds.map((setId, index) => ({ id: setId, label: setTitles[index] || setId }))
                : [{ id: '', label: title }],
        [setIds, setTitles, title]
    );
    const selectedPaper =
        paperOptions.find((paper) => paper.id === selectedSetId) ?? paperOptions[0];

    useEffect(() => {
        const closeMenus = (event: MouseEvent) => {
            if (commandBarRef.current?.contains(event.target as Node)) return;
            setIsSolutionOpen(false);
            setIsDownloadOpen(false);
        };
        document.addEventListener('mousedown', closeMenus);
        return () => document.removeEventListener('mousedown', closeMenus);
    }, []);

    const toggleMenu = (menu: 'solution' | 'download') => {
        setIsSolutionOpen((open) => (menu === 'solution' ? !open : false));
        setIsDownloadOpen((open) => (menu === 'download' ? !open : false));
    };

    return (
        <header className="pod-v2-command-bar" ref={commandBarRef}>
            <div className="pod-v2-command-bar__brand">
                <Link aria-label="스마트 문제은행 홈으로 이동" to="/create">
                    <img alt="스마트 문제은행" src={podLogo} />
                </Link>
            </div>

            <div className="pod-v2-command-bar__actions">
                <Dropdown
                    btnName={selectedPaper.label}
                    className="pod-v2-command-bar__paper-select"
                    disabled={disabled}
                    key={`${selectedPaper.id}:${selectedPaper.label}`}
                    onChange={(setId: string) => onSelectSet(setId)}
                    option={paperOptions.map((paper) => ({ name: paper.label, val: paper.id }))}
                    selected={selectedPaper.id}
                />

                <div className="pod-v2-command-bar__tabs" role="tablist" aria-label="자료 유형">
                    <button
                        aria-selected={sheet === 'question'}
                        className={sheet === 'question' ? 'is-active' : ''}
                        disabled={disabled}
                        onClick={() => onSheetChange('question')}
                        role="tab"
                        type="button"
                    >
                        문제지
                    </button>
                    <button
                        aria-selected={sheet === 'answer'}
                        className={sheet === 'answer' ? 'is-active' : ''}
                        disabled={disabled}
                        onClick={() => onSheetChange('answer')}
                        role="tab"
                        type="button"
                    >
                        해설지
                    </button>
                </div>

                <div
                    className={`pod-v2-command-bar__solution${
                        isEnglishSubject ? ' has-translation' : ''
                    }${isSolutionOpen ? ' is-open' : ''}`}
                >
                    <button
                        aria-expanded={isSolutionOpen}
                        className="pod-v2-command-bar__solution-trigger"
                        disabled={disabled}
                        onClick={() => toggleMenu('solution')}
                        type="button"
                    >
                        <span>
                            <small>해설지 구성</small>
                            <strong>
                                {[
                                    includeAnswer && '정답',
                                    includeExplanation && '해설',
                                    isEnglishSubject && includeTranslation && '해석',
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </strong>
                        </span>
                        <ChevronIcon />
                    </button>
                    <div
                        aria-label="해설지 구성"
                        className="pod-v2-command-bar__solution-menu"
                        role="dialog"
                    >
                        <strong className="pod-v2-command-bar__menu-title">해설지 구성</strong>
                        <div className="pod-v2-command-bar__check-grid">
                            <label className={includeAnswer ? 'is-checked' : ''}>
                                <input
                                    checked={includeAnswer}
                                    disabled={includeAnswer && !includeExplanation}
                                    onChange={(event) => onAnswerChange(event.target.checked)}
                                    type="checkbox"
                                />
                                <span aria-hidden="true">{includeAnswer ? '✓' : ''}</span>
                                <strong>정답</strong>
                            </label>
                            <label className={includeExplanation ? 'is-checked' : ''}>
                                <input
                                    checked={includeExplanation}
                                    disabled={includeExplanation && !includeAnswer}
                                    onChange={(event) => onExplanationChange(event.target.checked)}
                                    type="checkbox"
                                />
                                <span aria-hidden="true">{includeExplanation ? '✓' : ''}</span>
                                <strong>해설</strong>
                            </label>
                            {isEnglishSubject && (
                                <label className={includeTranslation ? 'is-checked' : ''}>
                                    <input
                                        checked={includeTranslation}
                                        onChange={(event) =>
                                            onTranslationChange(event.target.checked)
                                        }
                                        type="checkbox"
                                    />
                                    <span aria-hidden="true">{includeTranslation ? '✓' : ''}</span>
                                    <strong>해석</strong>
                                </label>
                            )}
                        </div>
                    </div>
                </div>

                {showDanControls && (
                    <div className="pod-v2-command-bar__columns" aria-label="단 설정">
                        <button
                            className={dan === 1 ? 'is-active' : ''}
                            disabled={disabled}
                            onClick={() => onDanChange(1)}
                            type="button"
                        >
                            1단
                        </button>
                        <button
                            className={dan === 2 ? 'is-active' : ''}
                            disabled={disabled}
                            onClick={() => onDanChange(2)}
                            type="button"
                        >
                            2단
                        </button>
                    </div>
                )}

                <div className={`pod-v2-command-bar__download${isDownloadOpen ? ' is-open' : ''}`}>
                    <button
                        aria-expanded={isDownloadOpen}
                        className="pod-v2-command-bar__download-trigger"
                        disabled={disabled}
                        onClick={() => toggleMenu('download')}
                        type="button"
                    >
                        <DownloadIcon />
                        다운로드
                        <ChevronIcon />
                    </button>
                    <div className="pod-v2-command-bar__download-menu" role="menu">
                        <button
                            onClick={() => {
                                onDownload('all');
                                setIsDownloadOpen(false);
                            }}
                            role="menuitem"
                            type="button"
                        >
                            <span className="pod-v2-command-bar__all-file-icon">
                                <img alt="" aria-hidden="true" src={downloadFileIcon} />
                            </span>
                            <strong>모든 형식</strong>
                        </button>
                        <div className="pod-v2-command-bar__download-divider" />
                        <button
                            onClick={() => {
                                onDownload('hwpx');
                                setIsDownloadOpen(false);
                            }}
                            role="menuitem"
                            type="button"
                        >
                            <img alt="" aria-hidden="true" src={hwpFileIcon} />
                            <strong>HWPX</strong>
                        </button>
                        <button
                            onClick={() => {
                                onDownload('pdf');
                                setIsDownloadOpen(false);
                            }}
                            role="menuitem"
                            type="button"
                        >
                            <img alt="" aria-hidden="true" src={pdfFileIcon} />
                            <strong>PDF</strong>
                        </button>
                        <button
                            onClick={() => {
                                onDownload('docx');
                                setIsDownloadOpen(false);
                            }}
                            role="menuitem"
                            type="button"
                        >
                            <img alt="" aria-hidden="true" src={wordFileIcon} />
                            <strong>DOCX</strong>
                        </button>
                    </div>
                </div>

                <span className="pod-v2-command-bar__status" role="status" title={status}>
                    {status}
                </span>
                <ContactPodButton
                    articleIds={[...new Set(articleOptions.map((item) => item.articleId))].join(
                        ','
                    )}
                    buttonText={
                        <>
                            <span aria-hidden="true">?</span>
                            문의
                        </>
                    }
                    publishData={publishData}
                    setsId={selectedSetId}
                    styles={{ button: 'pod-v2-command-bar__inquiry' }}
                    renderPopup={(onSave) => (
                        <PodV2ContactPopup
                            articleOptions={articleOptions}
                            currentTitle={selectedPaper.label}
                            onSave={onSave}
                        />
                    )}
                />
                <button
                    aria-label="나가기"
                    className="pod-v2-command-bar__exit"
                    onClick={() => window.location.assign(exitUrl)}
                    type="button"
                >
                    <ExitIcon />
                </button>
            </div>
        </header>
    );
};
